/* =========================================================================
 * /use-cases — research applications powered by Bripplex sensor data.
 *
 * Each case is anchored on the page (e.g. /use-cases#wave-tracking) so the
 * news cards on the mobile dashboard can deep-link to a specific one.
 *
 * Pattern reused from /docs/what + /researchers: DefaultNavbar + SiteFooter,
 * hero section, then a list of `UseCase` blocks with consistent geometry.
 * Designed to grow — add a new entry to the USE_CASES array, drop a hero
 * image alongside it, and a new section appears.
 * ========================================================================= */

import PropTypes from "prop-types";

import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import SiteFooter from "components/SiteFooter";

import consumerRoutes from "consumerRoutes";
import footerRoutes from "footer.routes";

import noiseMappingHero from "assets/images/use-cases/noise-mapping-hero.png";
import waveTrackingHero from "assets/images/use-cases/wave-tracking-hero.png";

// ---------------------------------------------------------------------------
// Use case data. Add new entries here — the page renders them in order.
// ---------------------------------------------------------------------------

const USE_CASES = [
  {
    id: "noise-mapping",
    eyebrow: "Real estate site selection",
    title: "Mapping neighborhood noise for residential planning",
    image: noiseMappingHero,
    imageAlt:
      "Illustration of a city skyline at dusk with microphone amplitude and location sensor traces drawn above it, plus calm and loud zones highlighted across neighborhoods.",
    problem:
      "Developers picking a site for a residential condo need ground-truth ambient noise across candidate neighborhoods. Acoustic survey crews are expensive, point-in-time, and miss the temporal pattern — morning rush, weekend nights, school drop-off, garbage day — that makes a block actually livable or not.",
    insight:
      "A smartphone's microphone reading amplitude only (no audio is ever captured) at 1 Hz, multiplied across residents over a 7-day window, paints a continuous heatmap of when and where the soundscape is calm or chaotic. Pair it with location at the same cadence and you can compare candidate blocks side by side — by hour, by day-of-week, by season.",
    sensors: [
      {
        icon: "graphic_eq",
        name: "Microphone (amplitude only)",
        detail:
          "1 Hz dB-equivalent readings — never raw audio. Captures the envelope of ambient noise without recording a single word of conversation.",
      },
      {
        icon: "place",
        name: "Location",
        detail:
          "Coarse geo at the same cadence so each amplitude sample is bound to a block. Aggregated across users it forms a noise heatmap of the neighborhood.",
      },
      {
        icon: "schedule",
        name: "Timestamp + day-of-week",
        detail:
          "Every sample is bucketed by hour-of-day and day-of-week — the dimension that turns a single average into a livability story (quiet weekends vs. brutal Monday mornings).",
      },
    ],
    beneficiaries: [
      {
        name: "Real estate developers",
        detail:
          "Compare candidate parcels objectively before acquisition — pick blocks that match the target buyer profile (family vs. nightlife-tolerant).",
      },
      {
        name: "Urban planners",
        detail:
          "Identify noise pollution hotspots for zoning, traffic-calming, and noise-barrier prioritization with continuous data rather than one-off surveys.",
      },
      {
        name: "Acoustic consultants",
        detail:
          "Augment short on-site measurements with weeks of crowd-sourced baseline data for environmental impact reports.",
      },
    ],
    callToAction: {
      heading: "Live in a city?",
      body: "Run a 7-day Neighborhood session. Your phone logs ambient amplitude only — never audio — and contributes to the noise map of your block.",
    },
  },
  {
    id: "wave-tracking",
    eyebrow: "Maritime weather modeling",
    title: "Wave tracking from sailors at sea",
    image: waveTrackingHero,
    imageAlt:
      "Illustration of a sailboat on the ocean with accelerometer, gyroscope, and device-motion sensor traces drawn above it.",
    problem:
      "Open-ocean weather forecasts depend on wave-state inputs that ground stations and satellites can only approximate. Buoy networks are sparse, expensive to maintain, and absent across most of the world's water.",
    insight:
      "Every smartphone on a moving boat is already an inertial measurement unit — accelerometer, gyroscope, and rotation sensors directly encode the wave motion the boat is sitting on. Capture that motion at scale and you have ground-truth wave data wherever there are sailors.",
    sensors: [
      {
        icon: "vibration",
        name: "Accelerometer",
        detail:
          "Heave (vertical wave height), surge, and sway — three-axis acceleration at 1 Hz captures the dominant wave period of the sea state the boat is in.",
      },
      {
        icon: "rotate_right",
        name: "Gyroscope",
        detail:
          "Pitch and roll rates — angular velocity reveals how steep and how often waves are passing under the hull.",
      },
      {
        icon: "3d_rotation",
        name: "Device motion (α/β/γ)",
        detail:
          "Orientation fusion of accelerometer + gyroscope + magnetometer — corrects for the boat's heading so wave data is directionally meaningful.",
      },
    ],
    beneficiaries: [
      {
        name: "PredictWind",
        detail:
          "Refines its open-water swell forecasts with crowd-sourced motion in regions where buoy coverage drops to zero.",
      },
      {
        name: "Windy.com",
        detail:
          "Uses inertial signals to validate WAM/SWAN wave model outputs against real ground-truth.",
      },
      {
        name: "ECMWF / NOAA",
        detail:
          "Public-good research datasets that improve global wave-forecast skill in under-observed regions.",
      },
    ],
    callToAction: {
      heading: "Are you a sailor?",
      body: "Start a Sailing session next time you head out. Your phone captures the motion automatically — no extra hardware, no setup.",
    },
  },
];

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const UseCases = () => {
  return (
    <>
      <DefaultNavbar routes={consumerRoutes} sticky />

      {/* Hero */}
      <MKBox
        component="section"
        sx={({ palette }) => ({
          background: `linear-gradient(180deg, ${palette.white.main} 0%, #EFF6FF 100%)`,
          pt: { xs: 12, md: 16 },
          pb: { xs: 6, md: 8 },
        })}
      >
        <Container>
          <Grid container justifyContent="center" textAlign="center">
            <Grid item xs={12} md={10} lg={9}>
              <MKTypography
                variant="overline"
                color="primary"
                fontWeight="bold"
                sx={{ letterSpacing: "0.1em" }}
              >
                Use cases
              </MKTypography>
              <MKTypography
                variant="h1"
                color="dark"
                mt={1.5}
                sx={({ breakpoints, typography: { size } }) => ({
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.1,
                  [breakpoints.down("md")]: { fontSize: size["3xl"] },
                })}
              >
                Real-world applications of community sensor data
              </MKTypography>
              <MKTypography
                variant="body1"
                color="text"
                mt={3}
                sx={{ maxWidth: 720, mx: "auto", fontSize: { xs: 16, md: 18 }, lineHeight: 1.6 }}
              >
                The 1&nbsp;Hz readings your phone contributes — motion, light, pressure, sound
                amplitude — turn into research datasets that ground stations can&apos;t reach.
                Here&apos;s what that looks like in practice.
              </MKTypography>
            </Grid>
          </Grid>
        </Container>
      </MKBox>

      {/* Use cases */}
      {USE_CASES.map((uc, i) => (
        <UseCaseSection key={uc.id} useCase={uc} isFirst={i === 0} />
      ))}

      <SiteFooter content={footerRoutes} />
    </>
  );
};

// ---------------------------------------------------------------------------
// Single use case section
// ---------------------------------------------------------------------------

const UseCaseSection = ({ useCase, isFirst }) => (
  <MKBox
    component="section"
    id={useCase.id}
    sx={({ palette }) => ({
      py: { xs: 8, md: 12 },
      px: { xs: 2, md: 4 },
      borderTop: isFirst ? "none" : `1px solid ${palette.grey[200]}`,
      // -80px so the sticky navbar doesn't cover the anchor target.
      scrollMarginTop: 80,
    })}
  >
    <Container maxWidth="lg">
      {/* Hero block — image + headline */}
      <Grid container spacing={{ xs: 3, md: 6 }} alignItems="center" mb={{ xs: 5, md: 8 }}>
        <Grid item xs={12} md={6}>
          <MKBox
            sx={({ borders }) => ({
              width: "100%",
              borderRadius: borders.borderRadius.lg,
              overflow: "hidden",
              boxShadow: "0 24px 48px -24px rgba(15, 23, 42, 0.35)",
              display: "block",
            })}
          >
            <MKBox
              component="img"
              src={useCase.image}
              alt={useCase.imageAlt}
              sx={{ width: "100%", height: "auto", display: "block" }}
            />
          </MKBox>
        </Grid>
        <Grid item xs={12} md={6}>
          <MKTypography
            variant="overline"
            color="primary"
            fontWeight="bold"
            sx={{ letterSpacing: "0.1em" }}
          >
            {useCase.eyebrow}
          </MKTypography>
          <MKTypography
            variant="h2"
            color="dark"
            mt={1}
            sx={({ breakpoints, typography: { size } }) => ({
              fontWeight: 700,
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
              [breakpoints.down("md")]: { fontSize: size["2xl"] },
            })}
          >
            {useCase.title}
          </MKTypography>
        </Grid>
      </Grid>

      {/* Problem + insight */}
      <Grid container spacing={{ xs: 3, md: 5 }} mb={{ xs: 5, md: 7 }}>
        <Grid item xs={12} md={6}>
          <SectionHeader>The gap</SectionHeader>
          <MKTypography
            variant="body1"
            color="text"
            sx={{ mt: 1.5, lineHeight: 1.7, fontSize: { xs: 15, md: 16 } }}
          >
            {useCase.problem}
          </MKTypography>
        </Grid>
        <Grid item xs={12} md={6}>
          <SectionHeader>How Bripplex helps</SectionHeader>
          <MKTypography
            variant="body1"
            color="text"
            sx={{ mt: 1.5, lineHeight: 1.7, fontSize: { xs: 15, md: 16 } }}
          >
            {useCase.insight}
          </MKTypography>
        </Grid>
      </Grid>

      {/* Sensors used */}
      <SectionHeader>Sensors at work</SectionHeader>
      <Grid container spacing={3} mt={0} mb={{ xs: 5, md: 7 }}>
        {useCase.sensors.map((s) => (
          <Grid item xs={12} md={4} key={s.name}>
            <MKBox
              sx={({ palette, borders }) => ({
                height: "100%",
                p: 3,
                borderRadius: borders.borderRadius.lg,
                border: `1px solid ${palette.grey[200]}`,
                background: palette.white.main,
              })}
            >
              <MKBox
                sx={({ palette }) => ({
                  width: 40,
                  height: 40,
                  borderRadius: "10px",
                  display: "grid",
                  placeItems: "center",
                  backgroundColor: "rgba(37, 99, 255, 0.1)",
                  color: palette.primary.main,
                  mb: 1.5,
                })}
              >
                <Icon fontSize="small">{s.icon}</Icon>
              </MKBox>
              <MKTypography
                variant="h6"
                color="dark"
                fontWeight="bold"
                sx={{ letterSpacing: "-0.01em" }}
              >
                {s.name}
              </MKTypography>
              <MKTypography variant="body2" color="text" sx={{ mt: 1, lineHeight: 1.6 }}>
                {s.detail}
              </MKTypography>
            </MKBox>
          </Grid>
        ))}
      </Grid>

      {/* Who benefits */}
      <SectionHeader>Who benefits</SectionHeader>
      <MKBox component="dl" sx={{ mt: 2, mb: { xs: 5, md: 7 } }}>
        {useCase.beneficiaries.map((b) => (
          <MKBox
            key={b.name}
            sx={{
              display: { xs: "block", sm: "grid" },
              gridTemplateColumns: "220px 1fr",
              py: 1.5,
              borderBottom: ({ palette }) => `1px dashed ${palette.grey[200]}`,
              gap: 2,
            }}
          >
            <MKTypography
              component="dt"
              variant="body2"
              sx={({ palette }) => ({
                fontWeight: 700,
                color: palette.dark.main,
              })}
            >
              {b.name}
            </MKTypography>
            <MKTypography
              component="dd"
              variant="body2"
              color="text"
              sx={{ m: 0, lineHeight: 1.6 }}
            >
              {b.detail}
            </MKTypography>
          </MKBox>
        ))}
      </MKBox>

      {/* CTA */}
      {useCase.callToAction && (
        <MKBox
          sx={({ borders }) => ({
            p: { xs: 3, md: 4 },
            borderRadius: borders.borderRadius.lg,
            background: "linear-gradient(135deg, #2563FF 0%, #38BDF8 100%)",
            color: "#FFFFFF",
            textAlign: "center",
          })}
        >
          <MKTypography
            variant="h5"
            sx={{ color: "#FFFFFF", fontWeight: 700, letterSpacing: "-0.01em" }}
          >
            {useCase.callToAction.heading}
          </MKTypography>
          <MKTypography
            variant="body1"
            sx={{ color: "rgba(255,255,255,0.92)", mt: 1.5, maxWidth: 560, mx: "auto" }}
          >
            {useCase.callToAction.body}
          </MKTypography>
        </MKBox>
      )}
    </Container>
  </MKBox>
);

const SectionHeader = ({ children }) => (
  <MKTypography
    variant="overline"
    color="primary"
    fontWeight="bold"
    sx={{ letterSpacing: "0.1em" }}
  >
    {children}
  </MKTypography>
);

SectionHeader.propTypes = {
  children: PropTypes.node.isRequired,
};

UseCaseSection.propTypes = {
  isFirst: PropTypes.bool.isRequired,
  useCase: PropTypes.shape({
    id: PropTypes.string.isRequired,
    eyebrow: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    imageAlt: PropTypes.string.isRequired,
    problem: PropTypes.string.isRequired,
    insight: PropTypes.string.isRequired,
    sensors: PropTypes.arrayOf(
      PropTypes.shape({
        icon: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        detail: PropTypes.string.isRequired,
      })
    ).isRequired,
    beneficiaries: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
        detail: PropTypes.string.isRequired,
      })
    ).isRequired,
    callToAction: PropTypes.shape({
      heading: PropTypes.string.isRequired,
      body: PropTypes.string.isRequired,
    }),
  }).isRequired,
};
